import { useMemo, useState } from 'react';
import {
  SPHERES,
  TARGET,
  THRESH,
  balance,
  lowSpheres,
  wheelHaiku,
  type SphereId,
} from '@superposition/domain';
import { useLoadedState } from '../../lib/store.js';
import { useSphereName } from '../../lib/names.js';
import { useLocale, useProfile } from '../../lib/profile.js';
import { haptic } from '../../lib/telegram.js';
import { sphereColor } from '../../lib/palette.js';
import { useT } from '../../i18n/index.js';

const CX = 160;
const CY = 160;
const MAXR = 118;

function pt(i: number, level: number): [number, number] {
  const ang = ((-90 + i * 45) * Math.PI) / 180;
  const r = MAXR * (level / TARGET);
  return [CX + r * Math.cos(ang), CY + r * Math.sin(ang)];
}

export function WheelScreen(): React.ReactNode {
  const sphereName = useSphereName();
  const { state, store } = useLoadedState();
  const profile = useProfile();
  const t = useT();
  const [active, setActive] = useState<SphereId | null>(null);

  const values = state.values;
  const low = useMemo(() => lowSpheres(values), [values]);
  const danger = low.length >= 2;
  const coh = useMemo(() => balance(values), [values]);

  const fixedToday = state.history.some((h) => h.date === store.today);

  const setValue = (id: SphereId, v: number): void => {
    store.update((prev) => ({ ...prev, values: { ...prev.values, [id]: v } }));
  };

  const fixDay = (): void => {
    haptic('medium');
    store.update((prev) => {
      const snap = { date: store.today, values: { ...prev.values } };
      const i = prev.history.findIndex((h) => h.date === store.today);
      // повторная фиксация обновляет этот день, а не добавляет второй
      const history =
        i >= 0
          ? prev.history.map((h, idx) => (idx === i ? snap : h))
          : [...prev.history, snap].sort((a, b) => (a.date < b.date ? -1 : 1));
      return { ...prev, history };
    });
  };

  const ringPoints = (level: number): string =>
    SPHERES.map((_, i) => pt(i, level).join(',')).join(' ');

  const polyPoints = SPHERES.map((s, i) => pt(i, values[s.id]).join(',')).join(' ');

  return (
    <section className="fade" aria-labelledby="wheel-title">
      <h2 className="screen-t" id="wheel-title">
        {profile?.name ? t.wheel.titleOf({ name: profile.name, gender: profile.gender }) : t.wheel.title}
      </h2>

      <div className="wheel-wrap">
        <svg className="wheel" viewBox="0 0 320 320" role="img" aria-label={t.wheel.aria}>
          {[2, 4, 6, 8, 10].map((L) => (
            <polygon
              key={L}
              className={`ring${L === THRESH ? ' threshold' : ''}`}
              points={ringPoints(L)}
            />
          ))}
          {SPHERES.map((s, i) => {
            const [x, y] = pt(i, TARGET);
            return (
              <line key={s.id} className="axis-line" x1={CX} y1={CY} x2={x} y2={y} />
            );
          })}
          <polygon
            points={polyPoints}
            fill={danger ? 'rgba(224,31,38,.20)' : 'rgba(246,244,240,.10)'}
            stroke={danger ? 'var(--red)' : 'var(--ink)'}
            strokeWidth={2}
          />
          {SPHERES.map((s, i) => {
            const [x, y] = pt(i, values[s.id]);
            const on = active === s.id;
            const isLow = values[s.id] < THRESH;
            return (
              <g key={s.id}>
                <circle
                  cx={x}
                  cy={y}
                  r={on ? 6 : 4.5}
                  fill={isLow ? 'var(--red)' : 'var(--ink)'}
                  {...(on ? { stroke: 'var(--red)', strokeWidth: 2.5 } : {})}
                />
                {/* отдельная прозрачная цель нажатия: палец попадает в 22px, а не в 4.5 */}
                <circle
                  className="vtx-hit"
                  cx={x}
                  cy={y}
                  r={16}
                  tabIndex={0}
                  role="button"
                  aria-label={t.wheel.vertexAria({ sphere: sphereName(s.id), value: values[s.id] })}
                  onClick={() => {
                    setActive(s.id);
                    document.getElementById(`sph-${s.id}`)?.focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActive(s.id);
                      document.getElementById(`sph-${s.id}`)?.focus();
                    }
                  }}
                />
              </g>
            );
          })}
          {active ? (
            <>
              <text className="center-val" x={CX} y={CY - 6} fontSize={40} fill="var(--red)">
                {values[active]}
              </text>
              <text
                className="center-lbl"
                x={CX}
                y={CY + 22}
                fill="var(--red)"
                style={{ letterSpacing: '1px' }}
              >
                {active ? sphereName(active) : null}
              </text>
            </>
          ) : (
            <>
              <text className="center-val" x={CX} y={CY - 4} fontSize={42} fill="var(--ink)">
                {coh}
                <tspan fontSize={18} fill="var(--muted)">
                  %
                </tspan>
              </text>
              <text className="center-lbl" x={CX} y={CY + 24}>
                {t.wheel.balanceIndex}
              </text>
            </>
          )}
        </svg>
      </div>

      <Haiku values={values} seed={store.today} />

      <div className="contour-h">{t.wheel.inner}</div>
      {SPHERES.filter((s) => s.grp === 'in').map((s) => (
        <SphereRow key={s.id} id={s.id} nm={sphereName(s.id)} value={values[s.id]} onChange={setValue} onFocus={setActive} />
      ))}

      <div className="contour-h">{t.wheel.outer}</div>
      {SPHERES.filter((s) => s.grp === 'out').map((s) => (
        <SphereRow key={s.id} id={s.id} nm={sphereName(s.id)} value={values[s.id]} onChange={setValue} onFocus={setActive} />
      ))}

      <button
        type="button"
        className={`fix-day${fixedToday ? ' done' : ''}`}
        onClick={fixDay}
      >
        {fixedToday ? t.wheel.dayFixed : t.wheel.fixDay}
      </button>
      <p className="lake-note" style={{ marginTop: 10 }}>
        {t.wheel.note}
      </p>
    </section>
  );
}

function SphereRow({
  id,
  nm,
  value,
  onChange,
  onFocus,
}: {
  id: SphereId;
  nm: string;
  value: number;
  onChange: (id: SphereId, v: number) => void;
  onFocus: (id: SphereId) => void;
}): React.ReactNode {
  const t = useT();
  const c = sphereColor(id);
  return (
    <div className="sph" style={{ color: c }}>
      <span className="dot" style={{ background: c }} />
      <label className="nm" htmlFor={`sph-${id}`}>
        {nm}
      </label>
      <input
        id={`sph-${id}`}
        type="range"
        min={0}
        max={10}
        step={1}
        value={value}
        style={{ ['--fill' as string]: `${(value / 10) * 100}%` }}
        onChange={(e) => onChange(id, Number(e.target.value))}
        onFocus={() => onFocus(id)}
        aria-label={nm}
        aria-valuetext={t.wheel.outOf10({ value })}
      />
      <span className="val" style={{ color: c }}>
        {value}
      </span>
    </div>
  );
}

/** короткий вывод по колесу в три строки, на образах книги. про деньги — только когда они просели */
function Haiku({ values, seed }: { values: Record<SphereId, number>; seed: string }): React.ReactNode {
  const sphereName = useSphereName();
  const locale = useLocale();
  const lines = wheelHaiku(values, sphereName, seed, locale);
  const low = lowSpheres(values).length > 0;
  return (
    <div className={`diag haiku${low ? ' low' : ''}`}>
      <div className="glyph" />
      <p className="txt">
        {lines.map((l, i) => (
          <span key={i}>{l}</span>
        ))}
      </p>
    </div>
  );
}
